import type { RefObject } from 'react'
import type { CaptionStyle, PlayerUiState, SettingsPanel } from '../types'
import { CastButton } from './CastButton'
import { InfoButton } from './InfoButton'
import { ProgressBar } from './ProgressBar'
import { SettingsMenu } from './SettingsMenu'
import { ShareButton } from './ShareButton'
import { TimeDisplay } from './TimeDisplay'
import { VolumeControl } from './VolumeControl'
import styles from './ControlsBar.module.css'

interface Props {
  ui: PlayerUiState
  visible: boolean
  thumbnails?: import('../types').ThumbnailsConfig
  videoRef?: RefObject<HTMLVideoElement | null>
  enableShare?: boolean
  enableSleep?: boolean
  enableCast?: boolean
  onTogglePlay: () => void
  onSeek: (t: number) => void
  onVolume: (v: number) => void
  onToggleMute: () => void
  onSpeed: (r: number) => void
  onQuality: (id: string) => void
  onAudio: (id: string) => void
  onPanel: (p: SettingsPanel) => void
  onToggleInfo: () => void
  onCaptionStyle: (s: CaptionStyle) => void
  onSleep: (m: number | 'end' | null) => void
  onShare?: (url: string) => void
  onCast?: (casting: boolean) => void
  onFullscreen: () => void
  onPip: () => void
  onTheater: () => void
  allowTheater?: boolean
  onCaptions: () => void
  onJumpLive: () => void
}

export function ControlsBar({
  ui,
  visible,
  thumbnails,
  videoRef,
  enableShare = true,
  enableSleep = true,
  enableCast = true,
  onTogglePlay,
  onSeek,
  onVolume,
  onToggleMute,
  onSpeed,
  onQuality,
  onAudio,
  onPanel,
  onToggleInfo,
  onCaptionStyle,
  onSleep,
  onShare,
  onCast,
  onFullscreen,
  onPip,
  onTheater,
  allowTheater = true,
  onCaptions,
  onJumpLive,
}: Props) {
  const isAd = ui.isAdPlaying
  const time = isAd ? ui.adCurrentTime : ui.currentTime
  const duration = isAd ? ui.adDuration : ui.duration
  const buffered = isAd ? ui.adCurrentTime : ui.buffered

  return (
    <div className={`${styles.bar} ${visible ? styles.visible : styles.hidden}`}>
      <ProgressBar
        currentTime={time}
        duration={duration}
        buffered={buffered}
        markers={isAd ? [] : ui.adMarkers}
        chapters={isAd ? [] : ui.chapters}
        thumbnails={isAd ? undefined : thumbnails}
        videoRef={videoRef}
        disabled={isAd}
        onSeek={onSeek}
      />

      <div className={styles.row}>
        <div className={styles.left}>
          <button
            type="button"
            className={styles.icon}
            onClick={onTogglePlay}
            aria-label={ui.playing ? 'Pause' : 'Play'}
          >
            {ui.playing ? (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M6 19h4V5H6v14zm8-14v14h4V5h-4z" />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M8 5v14l11-7z" />
              </svg>
            )}
          </button>

          <VolumeControl
            volume={ui.volume}
            muted={ui.muted}
            onVolume={onVolume}
            onToggleMute={onToggleMute}
          />

          <TimeDisplay current={time} duration={ui.isLive && !isAd ? 0 : duration} />
          {ui.isLive && !isAd && (
            <button type="button" className={styles.live} onClick={onJumpLive}>
              LIVE
            </button>
          )}
          {!isAd && <span className={styles.qualityChip}>{ui.qualityLabel}</span>}
          {!isAd && ui.sleepRemainingSec != null && (
            <span className={styles.sleepChip}>
              Sleep {Math.floor(ui.sleepRemainingSec / 60)}:
              {String(ui.sleepRemainingSec % 60).padStart(2, '0')}
            </span>
          )}
          {!isAd && ui.sleepLabel === 'End of video' && (
            <span className={styles.sleepChip}>Sleep @ end</span>
          )}
          {isAd && <span className={styles.adLabel}>Advertisement</span>}
        </div>

        <div className={styles.right}>
          {!isAd && enableShare && (
            <ShareButton currentTime={ui.currentTime} onShared={onShare} />
          )}

          {!isAd && enableCast && videoRef && (
            <CastButton videoRef={videoRef} onCast={onCast} />
          )}

          {!isAd && (
            <InfoButton
              open={ui.infoOpen}
              onToggle={onToggleInfo}
              title={ui.videoTitle}
              description={ui.videoDescription}
              views={ui.views}
            />
          )}

          {!isAd && (
            <SettingsMenu
              panel={ui.settingsPanel}
              onPanel={onPanel}
              qualityLevels={ui.qualityLevels}
              activeQualityId={ui.activeQualityId}
              qualityLabel={ui.qualityLabel}
              playbackRate={ui.playbackRate}
              captionsOn={ui.captionsOn}
              hasCaptions={ui.captionTracks.length > 0}
              captionStyle={ui.captionStyle}
              audioTracks={ui.audioTracks}
              activeAudioId={ui.activeAudioId}
              onQuality={onQuality}
              onSpeed={onSpeed}
              onCaptions={onCaptions}
              onCaptionStyle={onCaptionStyle}
              onAudio={onAudio}
              sleepEnabled={enableSleep}
              sleepLabel={ui.sleepLabel}
              sleepRemainingSec={ui.sleepRemainingSec}
              onSleep={onSleep}
            />
          )}

          {!isAd && allowTheater && (
            <button type="button" className={styles.icon} onClick={onTheater} aria-label="Theater mode">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path fill="currentColor" d="M19 6H5c-1.1 0-2 .9-2 2v8c0 1.1.9 2 2 2h14c1.1 0 2-.9 2-2V8c0-1.1-.9-2-2-2zm0 10H5V8h14v8z" />
              </svg>
            </button>
          )}

          {!isAd && (
            <button type="button" className={styles.icon} onClick={onPip} aria-label="Picture in picture">
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M19 7h-8v6h8V7zm2-4H3c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h18c1.1 0 2-.9 2-2V5c0-1.1-.9-2-2-2zm0 16H3V5h18v14z"
                />
              </svg>
            </button>
          )}

          <button
            type="button"
            className={styles.icon}
            onClick={onFullscreen}
            aria-label={ui.fullscreen ? 'Exit fullscreen' : 'Fullscreen'}
          >
            {ui.fullscreen ? (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z"
                />
              </svg>
            ) : (
              <svg viewBox="0 0 24 24" width="24" height="24">
                <path
                  fill="currentColor"
                  d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z"
                />
              </svg>
            )}
          </button>
        </div>
      </div>
    </div>
  )
}
